import React, { useEffect, useMemo, useState, useRef } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  SelectChangeEvent,
} from '@mui/material';
import { IconArrowRight, IconBack } from '../../../icons/Icon';
import SingleSelect from '../../../components/commonComponent/SingleSelect';
import styles from './plansetup.module.scss';
import MultiSelectNew from '../../../components/MultiSelectNew';
import { useNavigate, useLocation } from 'react-router-dom';
import { BASE_URL } from '../../../constants/apiConstants';
import axiosInstance from '../../../api/axios';
import { toast } from 'react-toastify';

const EditUserConfiguration: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [selectedOptions, setSelectedOptions] = useState<{
    sector: any;
    region: string[];
    menus: string[];
    sites: string[];
    roles: string[];
    cpusite: string;
    mainSite: string;
  }>({
    sector: '',
    region: [],
    menus: [],
    sites: [],
    roles: [],
    cpusite: '',
    mainSite: '',
  });

  const [CPUMapped, setCPUMapped] = useState(data?.data?.iscpumapped ? 'yes' : 'no');
  const [isCostFieldVisible, setIsCostFieldVisible] = useState('');
  // Applicable scope for Dish/Item selection (UI only for now)
  const [applicableScope, setApplicableScope] = useState<'d' | 'i'>('d');
  const location = useLocation();
  const segments = location.pathname.split('/');
  const action = segments[2];
  const id = segments[3];
  const [sitesPerCPU, setSitesPerCPU] = useState<{ [cpusite: string]: string[] }>({});
  const [mappedState, setMappedState] = useState({
    yes: { cpusite: '', sites: [] as string[], mainSite: '' },
    no: { cpusite: '', sites: [] as string[], mainSite: '' },
  });

  // Track original API state to restore when user toggles back to original scope
  const originalApiScopeRef = useRef<'d' | 'i'>('d');
  const originalApiMenusRef = useRef<{
    dish: string[],
    item: string[]
  }>({ dish: [], item: [] });
  const isInitialLoadRef = useRef(true);

  const goBack = () => {
    navigate(-1);
  };  

  useEffect(() => {
    const editUsers = async () => {
      try {
        const res = await axiosInstance.post(
          `${BASE_URL}/api/v1/User/GetUserDetailsByUserID`,
          { userid: id }
        );
        setData(res.data); 
      } catch (error) {
        data?.data?.error('Error fetching users:', error);
      }
    };
    editUsers();
  }, [id]);


  useEffect(() => {
    if (data?.data) {     
      setCPUMapped(data?.data?.iscpumapped ? 'yes' : 'no');
      setIsCostFieldVisible(data?.data?.isactive === 'Y' ? 'yes' : 'no');
      
      // Capture original API state for scope and menus
      const originalScope = data?.data?.isitemwise ? 'i' : 'd';
      originalApiScopeRef.current = originalScope;
      
      // Store original menu selections for both scopes
      originalApiMenusRef.current = {
        dish: (data?.data?.applicablemenulist || []).filter((item: any) => item.isselected).map((item: any) => item.menuplantypecode),
        item: (data?.data?.applicablemenulistitemwise || []).filter((item: any) => item.isselected).map((item: any) => item.menuplantypecode)
      };
      
      // Set applicable scope based on API value `isitemwise`
      setApplicableScope(originalScope);
      
      const selectedSites =
      data?.data?.iscpumapped === true
        ? data?.data?.sitelist
            //?.filter((item: any) => item.cpusitecode === data?.data?.cpusitecode)
            ?.filter((item: any) => item.isselected)
            .map((item: any) => item.sitecode)
        : data?.data?.sitelist?.filter((item: any) => item.isselected).map((item: any) => item.sitecode);

      const newSelectedOptions = {
        sector: data?.data?.sectorlist?.find((item: any) => item.isselected)?.sectorcode || '',
        region: data?.data?.regionlist
          ?.filter((item: any) => item.isselected && item.sectorcode === data?.data?.sectorlist?.find((s: any) => s.isselected)?.sectorcode)
          .map((item: any) => item.regionid) || [],
        menus: originalScope === 'i' ? originalApiMenusRef.current.item : originalApiMenusRef.current.dish,
        sites: selectedSites || [],
        roles: data?.data?.roleslist?.filter((item: any) => item.isselected).map((item: any) => item.roleid) || [],
        cpusite: data?.data?.iscpumapped && data?.data?.cpusitelistlist?.find((item: any) => item.isselected)?.cpusitecode || '',
        mainSite: data?.data?.usermainsitecode || '',
      };
      setSelectedOptions(newSelectedOptions);
      
      // Mark initial load as complete
      isInitialLoadRef.current = false;
    }
  }, [data?.data]);

  // When the user manually changes the applicable scope radio, always clear menus so
  // they start fresh with the new scope's options. Initial load is handled separately
  // via the data?.data useEffect above, so this handler is only wired to the RadioGroup.
  const handleApplicableScopeChange = (newScope: 'd' | 'i') => {
    setApplicableScope(newScope);
    setSelectedOptions(prev => ({ ...prev, menus: [] }));
  };
  
  useEffect(() => {
    if (!data?.data?.regionlist || !selectedOptions.sector) return;

    const validRegionIds = data.data.regionlist
      .filter((item: any) => item.isselected && item.sectorcode === selectedOptions.sector)
      .map((item: any) => item.regionid);

    // Only update if there is a difference (to prevent flickering)
    const regionChanged =
      validRegionIds.length !== selectedOptions.region.length ||
      validRegionIds.some((id:any, i:any) => id !== selectedOptions.region[i]);

    if (regionChanged) {
      setSelectedOptions((prev) => ({
        ...prev,
        region: validRegionIds,
      }));
  }
}, [selectedOptions.sector, data?.data?.regionlist]);

  
  const regionOptions = useMemo(() => {
  if (!selectedOptions.sector) return [];
  return (data?.data?.regionlist || [])
    .filter((item: any) => item.sectorcode === selectedOptions.sector)
    .map((item: any) => ({
      label: item.regionname,
      value: item.regionid,
      code: item.regionid,
      id: item.regionid,
    }));
}, [data?.data?.regionlist, selectedOptions.sector]);
  

  const sectorOptions = useMemo(() =>
    (data?.data?.sectorlist || []).map((item: any) => ({
      label: item.sectorname,
      value: item.sectorcode,
      code: item.sectorcode,
      id: item.sectorcode,
    })), [data?.data?.sectorlist]);

  const cpuOptions = useMemo(() =>
    (data?.data?.cpusitelistlist || []).map((item: any) => ({
      label: item.sitealias,
      value: item.cpusitecode,
      code: item.cpusitecode,
      id: item.cpusitecode,
    })), [data?.data?.cpusitelistlist]);

  // Helpers to validate selected values against options to avoid MUI warnings
  const validSingle = (options: any[], value: any) => {
    // Always return '' if options are not loaded or empty to prevent MUI out-of-range warnings
    if (!options || options.length === 0) return '';
    return options.some((o: any) => o.value === value) ? value : '';
  }

  const validMulti = (options: any[], values: any[]) => {
    // Always return [] if options are not loaded or empty
    if (!options || options.length === 0) return [];
    const set = new Set(options.map((o: any) => o.value));
    return (values || []).filter((v: any) => set.has(v));
  }
  // menu options depend on current applicable scope (dish/item)
  const menuOptions = useMemo(() => {
    const source = applicableScope === 'i' ? data?.data?.applicablemenulistitemwise : data?.data?.applicablemenulist;
    return ((source) || []).map((item: any) => ({
      label: item.menuplantypename,
      value: item.menuplantypecode,
      code: item.menuplantypecode,
      id: item.menuplantypecode,
    }));
  }, [data?.data?.applicablemenulist, data?.data?.applicablemenulistitemwise, applicableScope]);

  const roleOptions = useMemo(() =>
    (data?.data?.roleslist || []).map((item: any) => ({
      label: item.rolename,
      value: item.roleid,
      code: item.roleid,
      id: item.roleid,
    })), [data?.data?.roleslist]);

    const siteOptions = useMemo(() => {
      const allSites = data?.data?.sitelist || [];
      return allSites.map((item: any) => ({
        label: item.sitealias,
        value: item.sitecode,
        code: item.sitecode,
        id: item.sitecode,
      }));
    }, [data?.data?.sitelist]);

    // For Main Site dropdown only: show label as 'CPU Hyderabad (119A)'
    const mainSiteOptions = useMemo(() => {
      const allSites = data?.data?.sitelist || [];
      return allSites.map((item: any) => ({
        label: `${item.sitealias} (${item.sitecode})`,
        value: item.sitecode,
        code: item.sitecode,
        id: item.sitecode,
      }));
    }, [data?.data?.sitelist]);
    

    const handleChange = (field: keyof typeof selectedOptions) => (event: SelectChangeEvent<string | number>) => {
      const value = event.target.value;
      if (field === 'cpusite') {
        // Save current sites for the previous CPU site
        setSitesPerCPU((prevMap:any) => ({
          ...prevMap,
          [selectedOptions.cpusite]: selectedOptions.sites,
        }));
        // Load sites for the newly selected CPU site (if any saved)
        setSelectedOptions((prev: any) => ({
          ...prev,
          cpusite: value,
          sites: sitesPerCPU[value] || [],
        }));
      } else if (field === 'mainSite') {
        setSelectedOptions((prev) => ({
          ...prev,
          mainSite: String(value),
        }));
      } else {
        setSelectedOptions((prev) => ({
          ...prev,
          [field]: value,
        }));
      }
    };

  const handleMultiSelectChange = (field: keyof typeof selectedOptions, selectedValues: string[]) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [field]: selectedValues,
    }));
  };

  const handleCPUMappedChange = (value: 'yes' | 'no') => {
    setMappedState((prev) => ({
      ...prev,
      [CPUMapped]: {
        cpusite: selectedOptions.cpusite,
        sites: selectedOptions.sites,
        mainSite: selectedOptions.mainSite,
      },
    }));
    const stored = mappedState[value] || { cpusite: '', sites: [], mainSite: '' };

    setSelectedOptions((prev) => ({
      ...prev,
      cpusite: stored.cpusite,
      sites: stored.sites,
      mainSite: stored.mainSite || '',
    }));

    setCPUMapped(value);
  };

  const isFormValid = selectedOptions.sector.length > 0 &&
  selectedOptions.region.length > 0 &&
  selectedOptions.menus.length > 0 &&
  selectedOptions.sites.length > 0 &&
  selectedOptions.roles.length > 0 &&
  selectedOptions.mainSite.length > 0;

  const handleSubmit = async () => {
    const payload = {
      userid: id,
      cmpsectorcodelist: [selectedOptions.sector], 
      regionidlist: selectedOptions.region.map(String), 
      roleidlist: selectedOptions.roles.map(String), 
      menuplantypecodelist: selectedOptions.menus, 
      cpusitecode: CPUMapped === 'yes' ? selectedOptions.cpusite : '0', 
      iscpumapped: CPUMapped === 'yes', 
      sitecodelist: selectedOptions.sites, 
      isactive: isCostFieldVisible === 'yes', 
      // send boolean isitemwise based on selected applicable scope ('i' => item-wise)
      isitemwise: applicableScope === 'i' ? true : false,
      UserMainSiteCode: selectedOptions.mainSite,
    };
    try {
      await axiosInstance.post(`${BASE_URL}/api/v1/User/InsertUserDetails`, payload);
      toast("User configuration Updated successfully", { type: "success" });
      navigate('/userconfiguration');
    } catch (err) {
      console.error("Submit failed:", err);
      toast("Failed to update user configuration", { type: "error" });
    }
  };
  return (
    <div className="content-wrapper">
      <h1 className="flex align-items-center">
        <button onClick={goBack} className="mr-10"><IconBack /></button> {action === "view" ? "View" : "Edit"} User Configuration : {data?.data?.username}
      </h1>
      <div className="white-box mb-12">
        <div className='inner-contant'>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <FormControl fullWidth>
              <div className="flex justify-content-between align-items-end flex-wrap">
                <div className={`${styles.leftBox} flex flex-wrap justify-content-between align-items-end`}>
                  <div className={`flex flex-column ${styles.multiSelect}`}>
                    <SingleSelect
                      label="Select Sector"
                      options={sectorOptions}
                      value={validSingle(sectorOptions, selectedOptions.sector) || ''}
                      onChange={handleChange('sector')}
                      placeholder="Sector"
                      disabled={action === "view" || isCostFieldVisible === 'no'}
                    />
                  </div>

                  <div className={`flex flex-column ${styles.multiSelect}`}>
                    <MultiSelectNew
                      label="Region"
                      options={regionOptions}
                      selectedValues={validMulti(regionOptions, selectedOptions.region)}
                      onApply={(selected) => handleMultiSelectChange('region', selected)}
                      showLabelOnly={true}
                      required={true}
                      placeholder="Region"
                      disabled={action === "view" || isCostFieldVisible === 'no'}
                    />
                  </div>

                  <div className={`flex ${styles.multiSelect}`}>
                    <div className={`flex flex-column ${styles.multiSelect}`}>
                      <label className='mb-6 d-block'>CPU Mapped</label>
                      <RadioGroup
                        name='row-radio-buttons-group'
                        className='flex flex-row align-items-center'
                        value={CPUMapped}
                        onChange={(e) => handleCPUMappedChange(e.target.value as 'yes' | 'no')}
                      >
                        <FormControlLabel value={'yes'} control={<Radio />} label={'Yes'} className='mr-10' disabled={action === "view"} />
                        <FormControlLabel value={'no'} control={<Radio />} label={'No'} disabled={action === "view"} />
                      </RadioGroup>
                    </div>
                    <SingleSelect
                      label="CPU Site"
                      options={cpuOptions}
                      value={validSingle(cpuOptions, selectedOptions.cpusite) || ''}
                      onChange={handleChange('cpusite')}
                      placeholder="CPU Site"
                      lableWithId={true}
                      disabled={action === "view" || CPUMapped !== 'yes' || isCostFieldVisible === 'no'}
                    />
                  </div>

                  <div className={`flex flex-column ${styles.multiSelect}`}>
                    <div className="flex align-items-center">
                      <label className='mb-6 d-block' style={{ marginRight: 12 }}>Applicable Dish & Item</label>
                      <RadioGroup
                        name='applicable-dish-item'
                        className='flex flex-row align-items-center'
                        value={applicableScope}
                        onChange={(e) => handleApplicableScopeChange(e.target.value as 'd' | 'i')}
                      >                      
                        <FormControlLabel value={'d'} control={<Radio />} label={'Dish'} className='mr-10' disabled={action === "view"} />
                        <FormControlLabel value={'i'} control={<Radio />} label={'Item'} disabled={action === "view"} />
                      </RadioGroup>
                    </div>
                    <MultiSelectNew
                      options={menuOptions}
                      label='Applicable Menu'
                      selectedValues={validMulti(menuOptions, selectedOptions.menus)}
                      onApply={(selected) => handleMultiSelectChange('menus', selected)}
                      showLabelOnly={true}
                      placeholder='Applicable Menu'
                      selectAllLabel="Select All Applicable Menus"
                      disabled={action === "view" || isCostFieldVisible === 'no'}
                    />
                  </div>

                 
                 

                  <div className={`flex flex-column ${styles.multiSelect}`}>
                    <MultiSelectNew
                      options={siteOptions}
                      label='Sites'
                      selectedValues={validMulti(siteOptions, selectedOptions.sites)}
                      onApply={(selected) => handleMultiSelectChange('sites', selected)}
                      required={true}
                      placeholder='Sites'
                      selectAllLabel="All Sites"
                      disabled={action === "view" || isCostFieldVisible === 'no'}
                    />
                  </div>
 
                  <div className={`flex flex-column ${styles.multiSelect}`}>
                    <MultiSelectNew
                      options={roleOptions}
                      label='Roles'
                      selectedValues={validMulti(roleOptions, selectedOptions.roles)}
                      onApply={(selected) => handleMultiSelectChange('roles', selected)}
                      showLabelOnly={true}
                      required={true}
                      placeholder='Roles'
                      disabled={action === "view" || isCostFieldVisible === 'no'}
                    />
                  </div>
                  <div className={`flex flex-column ${styles.multiSelect}`}>
                    <SingleSelect
                      label="Main Site"
                      options={mainSiteOptions}
                      value={validSingle(mainSiteOptions, selectedOptions.mainSite) || ''}
                      onChange={handleChange('mainSite')}
                      placeholder="Main Site"
                      disabled={action === "view" || isCostFieldVisible === 'no'}
                    />
                  </div>

                  <div className={`flex flex-column ${styles.multiSelect}`}>
                    <label className='mb-6'>Is Active</label>
                    <RadioGroup
                      name='row-radio-buttons-group'
                      className='flex flex-row align-items-center'
                      value={isCostFieldVisible}
                      onChange={(e) => setIsCostFieldVisible(e.target.value)}
                    >
                      <FormControlLabel value={'yes'} control={<Radio />} label={'Yes'} className='mr-10' disabled={action === "view"} />
                      <FormControlLabel value={'no'} control={<Radio />} label={'No'} disabled={action === "view"} />
                    </RadioGroup>
                  </div>

                </div>

                <Button onClick={handleSubmit} className={`primary-btn ${styles.primaryBtn}`} disabled={action === "view" || !isFormValid}>
                  Go <IconArrowRight />
                </Button>
              </div>
            </FormControl>
          </LocalizationProvider>
        </div>
      </div>
    </div>
  );
};

export default EditUserConfiguration;
